import { useEffect, useState, useCallback, useRef } from "react";
import client from "@/lib/api";
import { visibleInterval } from "@/lib/poll";
import { Avatar } from "@/components/shared/UI";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useChat } from "@/context/ChatContext";
import { NewChatDialog } from "@/components/chat/NewChatDialog";
import { Search, Plus, ListChecks, Users, X, Archive, ArchiveRestore, BellOff, Repeat, Mail, Megaphone, TrendingUp } from "lucide-react";
import { recurLabel } from "@/lib/format";

const TABS = [["all", "All"], ["unread", "Unread"], ["groups", "Groups"], ["archived", "Archived"]];

function timeShort(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export function ChatList({ activeId, onSelect, paused = false }) {
  const { tick, bump } = useChat();
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [showNew, setShowNew] = useState(false);

  // When searching, look across ALL chats (incl. archived) regardless of the active tab.
  const searching = q.trim().length > 0;
  const archivedSeen = useRef(new Set());
  const load = useCallback(() => {
    const params = (tab === "archived" || searching) ? { include_archived: true } : { filter: tab };
    client.get("/chats", { params }).then(({ data }) => {
      if (tab !== "archived" || searching) { setRows(data); return; }
      const revived = data.find((c) => !c.archived && c.unread > 0 && archivedSeen.current.has(c.id));
      const archived = data.filter((c) => c.archived);
      archivedSeen.current = new Set(archived.map((c) => c.id));
      if (revived) {
        toast.info(`New message in ${revived.name} – moved back to your active chats`);
        setTab("all");
        return;
      }
      setRows(archived);
    }).catch(() => setRows([]));
  }, [tab, searching]);

  useEffect(() => { load(); }, [load, tick]);
  useEffect(() => { if (paused) return undefined; load(); return visibleInterval(load, 4000); }, [load, paused]);

  const needle = q.trim().toLowerCase();
  const filtered = (rows || []).filter((c) => {
    if (!needle) return true;
    const hay = [
      c.name || "",
      c.last_message_text || "",
      ...(c.members || []).map((m) => m.name || ""),
    ].join(" ").toLowerCase();
    return hay.includes(needle);
  });

  const archive = async (c, e) => {
    e.stopPropagation();
    try {
      const payload = c.archived ? { archived: false, archived_everyone: false } : { archived: true };
      await client.patch(`/chats/${c.id}`, payload);
      toast.success(c.archived ? "Chat restored" : "Chat archived for you");
      bump();
      load();
    } catch (err) { toast.error("Could not update chat"); }
  };

  return (
    <div className="flex h-full flex-col" data-testid="chat-list">
      <div className="border-b border-slate-100 p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search chats & messages…" data-testid="chat-search"
            className="w-full rounded-full border border-slate-200 bg-slate-50 py-2 pl-9 pr-9 text-sm focus:border-sky-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-100" />
          {q && (
            <button onClick={() => setQ("")} data-testid="chat-search-clear"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600"><X className="h-3.5 w-3.5" /></button>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-1">
          {TABS.map(([val, label]) => (
            <button key={val} onClick={() => setTab(val)} data-testid={`chat-tab-${val}`}
              className={cn("rounded-full px-2 py-1 text-xs font-semibold transition-colors",
                tab === val ? "bg-sky-100 text-sky-700" : "text-slate-500 hover:bg-slate-100")}>
              {label}
            </button>
          ))}
          <button onClick={() => setShowNew(true)} data-testid="chat-new-button"
            className="ml-auto flex shrink-0 items-center gap-1 rounded-full bg-sky-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-sky-700">
            <Plus className="h-3.5 w-3.5" />New
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        {rows === null ? (
          <p className="px-4 py-10 text-center text-sm text-slate-400">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-slate-400">{searching ? "No matches" : tab === "archived" ? "No archived chats" : "No chats yet"}</p>
        ) : (
          filtered.map((c) => (
            <div key={c.id} role="button" tabIndex={0} onClick={() => onSelect(c)} data-testid={`chat-list-item-${c.id}`}
              className={cn("group relative flex w-full items-center gap-3 border-b border-slate-50 px-3 py-2.5 text-left transition-colors",
                activeId === c.id ? "bg-sky-50" : "hover:bg-slate-50")}>
              <div className="relative shrink-0">
                <Avatar name={c.name} className="h-10 w-10" />
                {c.opportunity_id && <span title="Opportunity chat" data-testid={`chat-list-opp-badge-${c.id}`} className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white ring-2 ring-white"><TrendingUp className="h-2.5 w-2.5" /></span>}
                {c.campaign_id && <span title={c.campaign_kind === "edm" ? "Direct campaign chat" : "Agent campaign chat"} data-testid={`chat-list-campaign-badge-${c.id}`} className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-white ring-2 ring-white">{c.campaign_kind === "edm" ? <Mail className="h-2.5 w-2.5" /> : <Megaphone className="h-2.5 w-2.5" />}</span>}
                {c.type === "task" && <span className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-violet-500 text-white ring-2 ring-white"><ListChecks className="h-2.5 w-2.5" /></span>}
                {c.type === "group" && <span className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white ring-2 ring-white"><Users className="h-2.5 w-2.5" /></span>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className={cn("truncate text-sm", c.unread > 0 ? "font-bold text-slate-900" : "font-medium text-slate-700")}>{c.name}</p>
                  {c.task_recurring && c.task_recurring !== "None" && (
                    <span className="flex shrink-0 items-center gap-0.5 rounded-full bg-sky-50 px-1.5 text-[10px] font-semibold text-sky-600" data-testid={`chat-recur-indicator-${c.id}`}>
                      <Repeat className="h-2.5 w-2.5" />{recurLabel(c.task_recurring, c.task_due_date)}
                    </span>
                  )}
                  {c.muted && <BellOff className="h-3 w-3 shrink-0 text-slate-400" aria-label="Muted" />}
                  <span className="ml-auto shrink-0 text-[11px] text-slate-400">{timeShort(c.last_message_at)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <p className={cn("truncate text-xs", c.unread > 0 ? "font-semibold text-slate-700" : "text-slate-400")}>{c.last_message_text || "No messages yet"}</p>
                  {c.unread > 0 && <span data-testid={`chat-item-unread-${c.id}`} className="ml-auto flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-sky-500 px-1 text-[10px] font-bold text-white">{c.unread > 9 ? "9+" : c.unread}</span>}
                </div>
              </div>
              <button onClick={(e) => archive(c, e)} data-testid={`chat-archive-item-${c.id}`} title={c.archived ? "Restore chat" : "Archive chat"}
                className="absolute right-2 top-2 rounded-md bg-white/90 p-1 text-slate-400 opacity-0 shadow-sm transition-opacity hover:text-sky-600 group-hover:opacity-100">
                {c.archived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
              </button>
            </div>
          ))
        )}
      </div>

      <NewChatDialog open={showNew} onClose={() => setShowNew(false)} onCreated={(c) => onSelect(c)} />
    </div>
  );
}
